/*
 * Copyright (C) 2017-2019 Dremio Corporation
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import * as React from "react";
import clsx from "clsx";
import { CheckBox } from "@ui5/webcomponents-react/CheckBox";

type CheckboxProps = {
  className?: string;
  checked?: boolean;
  disabled?: boolean;
  label?: string;
  /** See `edge` on `components/Checkbox`. */
  edge?: "start";
};

/**
 * Kept separate from `components/Checkbox` because this entry point is built by
 * rollup from `src/` and cannot reach outside it.
 *
 * The label is handed to the web component rather than wrapped around it: an
 * enclosing `<label>` retargets its activation click onto the host element,
 * which UI5 ignores because it only listens inside its own shadow root.
 */
export const Checkbox = ({
  label,
  edge,
  className,
  ...props
}: CheckboxProps) => (
  <CheckBox
    {...props}
    className={clsx(
      className,
      // Mirrors the literal class used in `components/Checkbox`.
      edge === "start" && "dremio-checkbox--edge-start",
    )}
    text={label}
  />
);
